import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import AddToWalletModal from "./AddToWalletModal";

/* ================= SMALL COMPONENT ================= */

const StatCard = ({ title, value }) => (
  <div className="border rounded-lg p-4 bg-white shadow-sm">
    <p className="text-sm text-gray-500">{title}</p>
    <p className="text-2xl font-bold mt-1">{value}</p>
  </div>
);

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

/* ================= MAIN COMPONENT ================= */

const PaymentsTab = ({ partner, transactions, onAddToWallet }) => {
  const [showModal, setShowModal] = useState(false);
  const [localTransactions, setLocalTransactions] =
    useState([]);
  const formatAmount = (value) =>
    Number(value || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    });

  const list = Array.isArray(transactions) ? transactions : localTransactions;

  const handleAddToWallet = ({ jobId, amount, reason }) => {
    const created = {
      date: new Date().toISOString().slice(0, 10),
      purpose: `${reason} (Job: ${jobId})`,
      reference: jobId,
      credit: Number(amount),
      debit: 0,
      penalty: 0,
      status: "completed",
    };

    if (onAddToWallet) {
      onAddToWallet(created);
      return;
    }

    setLocalTransactions((prev) => {
      const nextId =
        prev.length > 0 ? Math.max(...prev.map((t) => t.id)) + 1 : 1;
      return [{ ...created, id: nextId }, ...prev];
    });
  };

  const { totalCredit, totalDebit, totalPenalties, walletBalance } =
    useMemo(() => {
      const totalCredit = list.reduce(
        (sum, t) => sum + (t.credit || 0),
        0
      );
      const totalDebit = list.reduce(
        (sum, t) => sum + (t.debit || 0),
        0
      );
      const totalPenalties = list.reduce(
        (sum, t) => sum + (t.penalty || 0),
        0
      );

      return {
        totalCredit,
        totalDebit,
        totalPenalties,
        walletBalance: totalCredit - totalDebit - totalPenalties,
      };
    }, [list]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-800">
            Wallet Transactions
          </h3>
          <p className="text-sm text-gray-600">
            {partner?.name ? `for ${partner.name}` : ""}
          </p>
        </div>
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm"
          onClick={() => setShowModal(true)}
        >
          + Add To Wallet
        </button>
      </div>
      {/* SUMMARY CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Wallet Balance"
          value={`₹${formatAmount(walletBalance)}`}
        />
        <StatCard
          title="Total Credit"
          value={`₹${formatAmount(totalCredit)}`}
        />
        <StatCard
          title="Total Debit"
          value={`₹${formatAmount(totalDebit)}`}
        />
        <StatCard
          title="Penalties"
          value={`₹${formatAmount(totalPenalties)}`}
        />
      </div>

      {/* PAYMENT TRANSACTIONS TABLE */}
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-2 text-left">Date</th>
              <th className="px-4 py-2 text-left">Purpose</th>
              <th className="px-4 py-2 text-left">Credit</th>
              <th className="px-4 py-2 text-left">Debit</th>
              <th className="px-4 py-2 text-left">Penalties</th>
              <th className="px-4 py-2 text-left">Reference</th>
              <th className="px-4 py-2 text-left">Status</th>
            </tr>
          </thead>

          <tbody>
            {list.map((txn) => (
              <tr key={txn.id} className="border-b">
                <td className="px-4 py-2">
                  {new Date(txn.date).toLocaleDateString()}
                </td>

                <td className="px-4 py-2">{txn.purpose}</td>

                <td className="px-4 py-2 font-semibold text-green-700">
                  {txn.credit > 0 ? `₹${formatAmount(txn.credit)}` : "-"}
                </td>

                <td className="px-4 py-2 font-semibold text-red-600">
                  {txn.debit > 0 ? `₹${formatAmount(txn.debit)}` : "-"}
                </td>

                <td className="px-4 py-2 font-semibold text-red-600">
                  {txn.penalty > 0 ? `₹${formatAmount(txn.penalty)}` : "-"}
                </td>

                <td className="px-4 py-2 font-medium">
                  {txn.reference || "-"}
                </td>

                <td className="px-4 py-2">
                  <span
                    className={`text-xs font-semibold px-2 py-1 rounded-full ${
                      txn.status === "completed"
                        ? "bg-green-100 text-green-700"
                        : "bg-yellow-100 text-yellow-700"
                    }`}
                  >
                    {txn.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {list.length === 0 && (
          <div className="p-4 text-gray-500">
            No payment transactions found for this partner.
          </div>
        )}
      </div>

      {showModal && (
        <AddToWalletModal
          onClose={() => setShowModal(false)}
          onSubmit={handleAddToWallet}
        />
      )}
    </div>
  );
};

PaymentsTab.propTypes = {
  partner: PropTypes.any,
  transactions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      date: PropTypes.string.isRequired,
      purpose: PropTypes.string.isRequired,
      reference: PropTypes.string,
      credit: PropTypes.number.isRequired,
      debit: PropTypes.number.isRequired,
      penalty: PropTypes.number.isRequired,
      status: PropTypes.string.isRequired,
    })
  ),
  onAddToWallet: PropTypes.func,
};

export default PaymentsTab;
